/**
 * ChatContainer Component
 *
 * Container component that wires together:
 * - ChatPanel (UI)
 * - chatStore (state management)
 * - useChatWebSocket (WebSocket connection to provider-aware chat route)
 *
 * This component handles the WebSocket lifecycle and message routing,
 * providing a complete chat experience with Claude or Codex.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ChatPanel } from '@/components/ChatPanel';
import { useChatStore } from '@/stores/chatStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useChatWebSocket, type ChatServerEvent } from '@/hooks/useWebSocket';
import {
  api,
  type ChatInteraction,
  type ChatInteractionSubmission,
  type SkillInfo,
} from '@/services/api';
import { cn } from '@/lib/utils';
import { releasesPendingSubmission } from '@/components/chatEventLifecycle';

const MAX_RECONNECT_ATTEMPTS = 5;

function getProviderLabel(provider: string | undefined): string {
  if (!provider) return 'Agent';
  if (provider === 'codex') return 'Codex';
  if (provider === 'claude') return 'Claude';
  return provider;
}

// ============================================
// Types
// ============================================

interface ChatContainerProps {
  /** Additional CSS classes */
  className?: string;
  onOpenStatus?: () => void;
}

// ============================================
// ChatContainer Component
// ============================================

export function ChatContainer({ className, onOpenStatus }: ChatContainerProps) {
  const {
    sessions,
    activeSession,
    activeSessionId,
    activeStreamsBySession,
    streamsById,
    addUserMessage,
    startStreaming,
    handleStreamEvent,
    selectSession,
  } = useChatStore();

  const { contextTimezone } = useSettingsStore();

  // Track current stream ID for cancellation
  const currentStreamIdRef = useRef<string | null>(null);
  const pendingSubmitSessionIdsRef = useRef<Set<string>>(new Set());
  const recoveredInteractionIdsRef = useRef<Set<string>>(new Set());
  const streamsByIdRef = useRef(streamsById);

  useEffect(() => {
    streamsByIdRef.current = streamsById;
  }, [streamsById]);

  // User-invocable Skills power composer discovery. Commands still execute in
  // the active chat; the application never creates a chat implicitly.
  const [availableSkills, setAvailableSkills] = useState<SkillInfo[]>([]);
  useEffect(() => {
    let cancelled = false;
    api.skills
      .list()
      .then((res) => {
        if (cancelled) return;
        const userInvocableSkills = res.skills.filter((skill) => skill.userInvocable !== false);
        setAvailableSkills(userInvocableSkills);
      })
      .catch(() => {
        /* no catalog → no command palette; messages still send normally */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle WebSocket messages from the selected provider
  const handleMessage = useCallback(
    (event: ChatServerEvent) => {
      if (event.type === 'interaction' && event.interaction && event.streamId) {
        const provider = event.provider === 'codex' ? 'codex' : 'claude';
        if (!streamsByIdRef.current[event.streamId]) {
          startStreaming(event.streamId, {
            forSessionId: event.interaction.sessionId,
            provider,
          });
        }
      }

      if (event.type === 'result') {
        const stream = event.streamId
          ? useChatStore.getState().streamsById[event.streamId]
          : null;
        const sessionId = stream?.sessionId;
        const session = sessionId ? sessions.find((item) => item.id === sessionId) : null;
        const providerLabel = getProviderLabel(event.provider ?? stream?.provider);
        const isActiveChat = Boolean(sessionId && sessionId === activeSessionId);

        toast.success(`${providerLabel} finished responding`, {
          description: session?.name,
          action: sessionId && !isActiveChat
            ? {
                label: 'Open',
                onClick: () => {
                  void selectSession(sessionId);
                },
              }
            : undefined,
        });
      }

      if (releasesPendingSubmission(event)) {
        const sessionId = event.streamId
          ? useChatStore.getState().streamsById[event.streamId]?.sessionId
          : null;
        if (sessionId) {
          pendingSubmitSessionIdsRef.current.delete(sessionId);
        }
      }

      handleStreamEvent(event);
    },
    [activeSessionId, handleStreamEvent, selectSession, sessions, startStreaming]
  );

  // Handle WebSocket connection
  const handleConnect = useCallback(() => {
    console.log('[ChatContainer] WebSocket connected');
  }, []);

  // Handle WebSocket disconnection
  const handleDisconnect = useCallback((event: CloseEvent) => {
    console.log('[ChatContainer] WebSocket disconnected:', event.code, event.reason);
  }, []);

  // Handle WebSocket errors
  const handleError = useCallback((event: Event) => {
    console.error('[ChatContainer] WebSocket error:', event);
  }, []);

  // Track connection toast ID so we can dismiss it when connection is restored
  const connectionToastId = useRef<string | number | null>(null);

  // Initialize provider-aware chat WebSocket
  const { send, isConnected, connect, reconnectAttempts } = useChatWebSocket({
    onMessage: handleMessage,
    onConnect: handleConnect,
    onDisconnect: handleDisconnect,
    onError: handleError,
    autoConnect: true,
    autoReconnect: true,
    maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
    debug: import.meta.env.DEV, // Enable debug in development
  });

  // Show connection lost toast after multiple failed attempts
  useEffect(() => {
    const isMaxedOut = reconnectAttempts >= MAX_RECONNECT_ATTEMPTS;
    const shouldShowToast = reconnectAttempts >= 2;

    if (isConnected) {
      // Connection restored - dismiss any existing toast
      if (connectionToastId.current) {
        toast.dismiss(connectionToastId.current);
        connectionToastId.current = null;
      }
    } else if (shouldShowToast && !connectionToastId.current) {
      // Show persistent connection toast
      connectionToastId.current = toast.error(
        isMaxedOut
          ? 'Connection lost. Please refresh the page.'
          : `Reconnecting... (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
        {
          duration: Infinity, // Stay until dismissed
          action: isMaxedOut
            ? {
                label: 'Refresh',
                onClick: () => window.location.reload(),
              }
            : undefined,
        }
      );
    } else if (shouldShowToast && connectionToastId.current) {
      // Update existing toast with new reconnect count
      toast.error(
        isMaxedOut
          ? 'Connection lost. Please refresh the page.'
          : `Reconnecting... (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
        {
          id: connectionToastId.current,
          duration: Infinity,
          action: isMaxedOut
            ? {
                label: 'Refresh',
                onClick: () => window.location.reload(),
              }
            : undefined,
        }
      );
    }
  }, [isConnected, reconnectAttempts]);

  // Reconnect WebSocket if disconnected when user tries to send a message
  const ensureConnected = useCallback(() => {
    if (!isConnected) {
      connect();
    }
  }, [isConnected, connect]);

  /**
   * Handle sending a message to the selected provider
   */
  const handleSendMessage = useCallback(
    async (message: string, images: string[] = []): Promise<boolean> => {
      // Ensure WebSocket is connected
      ensureConnected();

      if (!activeSessionId) {
        return false;
      }

      const submitSessionId = activeSessionId;

      if (activeStreamsBySession[submitSessionId]) {
        return false;
      }

      if (pendingSubmitSessionIdsRef.current.has(submitSessionId)) {
        return false;
      }
      pendingSubmitSessionIdsRef.current.add(submitSessionId);

      if (!activeSession || !activeSession.canPost) {
        if (!activeSession) console.error('[ChatContainer] No active session');
        pendingSubmitSessionIdsRef.current.delete(submitSessionId);
        return false;
      }

      // Add user message to the store (optimistic update)
      await addUserMessage(message, images);

      // Generate stream ID
      const newStreamId = `stream-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      currentStreamIdRef.current = newStreamId;

      const provider = activeSession.provider === 'codex' ? 'codex' : 'claude';
      const claudeSessionId = provider === 'claude' ? activeSession.claudeSessionId : undefined;

      // Mark streaming as started (adds placeholder message)
      startStreaming(newStreamId, { forSessionId: submitSessionId, provider });

      if (import.meta.env.DEV) {
        if (claudeSessionId) {
          console.log('[ChatContainer] Using --resume with Claude session ID:', claudeSessionId);
        } else {
          console.log(`[ChatContainer] Starting new ${provider} session`);
        }
      }

      // Send start message to WebSocket
      const sent = send({
        type: 'start',
        provider,
        message,
        streamId: newStreamId,
        sessionId: claudeSessionId || undefined,
        chatSessionId: activeSession.id,
        images,
        language: 'auto',
        timezone: contextTimezone,
        model: activeSession.model || undefined,
        reasoningEffort: activeSession.reasoningEffort || undefined,
      });

      if (!sent) {
        pendingSubmitSessionIdsRef.current.delete(submitSessionId);
        console.error('[ChatContainer] Failed to send message via WebSocket');
        // The handleStreamEvent will handle the error state
        handleStreamEvent({
          type: 'error',
          error: `Failed to connect to ${provider === 'codex' ? 'Codex' : 'Claude'}. Please try again.`,
          streamId: newStreamId,
        });
        return false;
      }

      return true;
    },
    [activeSession, activeSessionId, activeStreamsBySession, ensureConnected, addUserMessage, startStreaming, send, handleStreamEvent, contextTimezone]
  );

  const handleSubmitInteraction = useCallback(
    async (
      interaction: ChatInteraction,
      submission: ChatInteractionSubmission
    ): Promise<boolean> => {
      ensureConnected();

      if (
        !activeSession
        || !activeSession.canPost
        || activeSession.id !== interaction.sessionId
        || (interaction.status !== 'pending' && interaction.status !== 'submitted')
        || activeStreamsBySession[interaction.sessionId]
        || pendingSubmitSessionIdsRef.current.has(interaction.sessionId)
      ) {
        return false;
      }

      const isRecovery = interaction.status === 'submitted';
      if (
        isRecovery
        && (!interaction.response || !interaction.resumeStreamId || interaction.resumeCompleted)
      ) {
        return false;
      }

      pendingSubmitSessionIdsRef.current.add(interaction.sessionId);
      const streamId = isRecovery
        ? interaction.resumeStreamId!
        : `interaction-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      currentStreamIdRef.current = streamId;
      const provider = activeSession.provider === 'codex' ? 'codex' : 'claude';

      startStreaming(streamId, {
        forSessionId: interaction.sessionId,
        provider,
      });

      const sent = send({
        type: 'submit_interaction',
        provider,
        streamId,
        interactionId: interaction.id,
        interactionRevision: interaction.revision,
        ...submission,
        language: 'auto',
        timezone: contextTimezone,
      });

      if (!sent) {
        pendingSubmitSessionIdsRef.current.delete(interaction.sessionId);
        handleStreamEvent({
          type: 'error',
          provider,
          streamId,
          error: 'Failed to submit the interaction. Please try again.',
        });
        return false;
      }

      return true;
    },
    [
      activeSession,
      activeStreamsBySession,
      contextTimezone,
      ensureConnected,
      handleStreamEvent,
      send,
      startStreaming,
    ]
  );

  useEffect(() => {
    if (!isConnected || !activeSession || !activeSession.canPost) return;
    if (activeStreamsBySession[activeSession.id]) return;
    if (pendingSubmitSessionIdsRef.current.has(activeSession.id)) return;

    const recoverable = activeSession.messages
      .map((message) => message.interaction)
      .find((interaction): interaction is ChatInteraction => Boolean(
        interaction
        && interaction.status === 'submitted'
        && interaction.response
        && interaction.resumeStreamId
        && !interaction.resumeCompleted
        && !recoveredInteractionIdsRef.current.has(interaction.id)
      ));

    if (!recoverable?.response) return;
    recoveredInteractionIdsRef.current.add(recoverable.id);
    void handleSubmitInteraction(
      recoverable,
      recoverable.kind === 'approval'
        ? { optionId: recoverable.response.optionId }
        : { definitions: recoverable.response.definitions }
    );
  }, [
    activeSession,
    activeStreamsBySession,
    handleSubmitInteraction,
    isConnected,
  ]);

  /**
   * Handle canceling the current stream
   */
  const handleCancelStream = useCallback(() => {
    const streamToCancel = activeSessionId ? activeStreamsBySession[activeSessionId] : currentStreamIdRef.current;
    if (streamToCancel) {
      send({
        type: 'cancel',
        streamId: streamToCancel,
      });
    }
  }, [activeSessionId, activeStreamsBySession, send]);

  useEffect(() => {
    if (!isConnected) return;

    for (const stream of Object.values(streamsByIdRef.current)) {
      send({
        type: 'attach',
        provider: stream.provider,
        streamId: stream.streamId,
      });
    }
  }, [isConnected, send]);

  return (
    <div className={cn('h-full', className)}>
      <ChatPanel
        onSendMessage={handleSendMessage}
        onSubmitInteraction={handleSubmitInteraction}
        onCancelStream={handleCancelStream}
        skills={availableSkills}
        onOpenStatus={onOpenStatus}
      />
    </div>
  );
}

export default ChatContainer;
